import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Upload, CreditCard, Download } from "lucide-react";
import Modal, { PageHeader, StatusBadge, EmptyState, GoldButton, GhostButton, Field, Input, Select } from "@/components/Ui";
import { fmtDate, fmtDateTime } from "@/lib/time";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Mirrors the constants in PortalOverview — kept in sync so the cards and
// list pages always represent the same business definition of "open" /
// "upcoming" / "outstanding".
const TERMINAL_CASE_STATUSES = new Set([
    "closed", "disposed", "withdrawn", "dismissed", "archived", "settled"
]);
const COMPLETED_HEARING_STATUSES = new Set([
    "completed", "heard", "disposed", "done"
]);

function loadRazorpay() {
    return new Promise((resolve) => {
        if (window.Razorpay) return resolve(true);
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
    });
}

export function PortalCases() {
    const [items, setItems] = useState([]);
    const [showDisposed, setShowDisposed] = useState(false);
    useEffect(() => { api.get("/portal/cases").then(r => setItems(r.data)); }, []);

    // Default view: only non-terminal litigations, sorted Z-A by created_at (newest first).
    const visible = useMemo(() => {
        const base = showDisposed
            ? [...items]
            : items.filter(c => !TERMINAL_CASE_STATUSES.has((c.status || "").toLowerCase()));
        return base.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    }, [items, showDisposed]);
    const disposedCount = items.length - items.filter(c => !TERMINAL_CASE_STATUSES.has((c.status || "").toLowerCase())).length;

    const ctrl = useTableControls(visible);
    return (
        <>
            <PageHeader title="My litigations"
                subtitle="Every matter your advocates are running for you — disposed matters hidden by default."/>
            {disposedCount > 0 && (
                <div className="mb-3 inline-flex items-center gap-3 text-xs text-muted-foreground border border-border px-3 py-2">
                    <span>{disposedCount} disposed/closed matter{disposedCount === 1 ? "" : "s"} hidden.</span>
                    <button onClick={() => setShowDisposed(s => !s)} data-testid="portal-cases-toggle-disposed"
                        className="text-foreground underline hover:no-underline">
                        {showDisposed ? "Hide them" : "Show them too"}
                    </button>
                </div>
            )}
            {visible.length === 0 ? (
                <EmptyState title="No open litigations" subtitle="As soon as your firm opens a case, it will appear here."/>
            ) : (
                <>
                <FilterChip ctrl={ctrl} className="mb-3"/>
                <div className="border border-border overflow-x-auto">
                    <table className="min-w-[640px] w-full text-sm">
                        <thead className="bg-muted border-b border-border">
                            <tr className="text-left">
                                <SortableTh field="internal_number" ctrl={ctrl}>No.</SortableTh>
                                <SortableTh field="title" ctrl={ctrl}>Title</SortableTh>
                                <SortableTh field="court" ctrl={ctrl}>Court</SortableTh>
                                <SortableTh field="stage" ctrl={ctrl}>Stage</SortableTh>
                                <SortableTh field="next_hearing" ctrl={ctrl}>Next hearing</SortableTh>
                                <SortableTh field="status" ctrl={ctrl}>Status</SortableTh>
                            </tr>
                        </thead>
                        <tbody>
                            {ctrl.rows.map(c => (
                                <tr key={c.id} className="border-b border-border hover:bg-muted/50">
                                    <td className="px-4 py-3 font-mono text-xs text-primary">
                                        <Link to={`/portal/cases/${c.id}`}>{c.internal_number}</Link>
                                    </td>
                                    <td className="px-4 py-3"><Link to={`/portal/cases/${c.id}`}>{c.title}</Link></td>
                                    <td className="px-4 py-3 text-muted-foreground">{c.court || "—"}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{c.stage}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{c.next_hearing ? fmtDate(c.next_hearing) : "—"}</td>
                                    <td className="px-4 py-3"><StatusBadge status={c.status}/></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                </>
            )}
        </>
    );
}

export function PortalCaseDetail() {
    const { id } = useParams();
    const [c, setC] = useState(null);
    useEffect(() => { api.get(`/portal/cases/${id}`).then(r => setC(r.data)); }, [id]);
    if (!c) return <div className="text-muted-foreground text-sm">Loading…</div>;
    return (
        <div className="space-y-8">
            <Link to="/portal/cases" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-2"><ArrowLeft className="w-4 h-4"/> Back to Litigations</Link>
            <div>
                <div className="font-mono text-xs text-primary mb-1">{c.internal_number}</div>
                <h1 className="font-serif text-3xl sm:text-4xl font-light">{c.title}</h1>
                <div className="text-sm text-muted-foreground mt-2">{c.court || "—"} · {c.category || "—"} · {c.type || "—"}</div>
                <div className="mt-3 flex items-center gap-2"><StatusBadge status={c.status}/><span className="text-xs text-muted-foreground">Stage: {c.stage}</span></div>
            </div>
            <div>
                <div className="overline mb-3">Hearings</div>
                {c.hearings?.length ? (
                    <div className="border border-border divide-y divide-border">
                        {c.hearings.map(h => (
                            <div key={h.id} className="px-4 py-3 flex items-center justify-between text-sm flex-wrap gap-2">
                                <div>
                                    <div className="font-mono text-xs text-muted-foreground">{fmtDateTime(h.scheduled_at)}</div>
                                    <div className="mt-1">{h.purpose || "Hearing"}</div>
                                </div>
                                <StatusBadge status={h.status}/>
                            </div>
                        ))}
                    </div>
                ) : <div className="text-sm text-muted-foreground">No hearings.</div>}
            </div>
            <div>
                <div className="overline mb-3">Documents</div>
                {c.documents?.length ? (
                    <div className="grid sm:grid-cols-2 gap-px bg-border">
                        {c.documents.map(d => (
                            <a key={d.id} href={`${BACKEND_URL}/api/documents/${d.id}/download`} target="_blank" rel="noreferrer"
                                className="bg-muted p-5 hover:bg-[#202020] transition-colors">
                                <div className="text-sm truncate">{d.original_filename}</div>
                                <div className="text-xs text-muted-foreground font-mono mt-1">{(d.size / 1024).toFixed(1)} KB</div>
                            </a>
                        ))}
                    </div>
                ) : <div className="text-sm text-muted-foreground">No documents.</div>}
            </div>
        </div>
    );
}

export function PortalHearings() {
    const [items, setItems] = useState([]);
    const [showCompleted, setShowCompleted] = useState(false);
    useEffect(() => { api.get("/portal/hearings").then(r => setItems(r.data)); }, []);

    const visible = useMemo(() => {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const upcoming = items.filter(
            h => !COMPLETED_HEARING_STATUSES.has((h.status || "").toLowerCase())
              && (!h.scheduled_at || new Date(h.scheduled_at) >= today)
        );
        const base = showCompleted ? [...items] : upcoming;
        return base.sort((a, b) => new Date(a.scheduled_at || 0) - new Date(b.scheduled_at || 0));   // A-Z
    }, [items, showCompleted]);
    const hiddenCount = items.length - items.filter(
        h => !COMPLETED_HEARING_STATUSES.has((h.status || "").toLowerCase())
          && (!h.scheduled_at || new Date(h.scheduled_at) >= new Date(new Date().setHours(0,0,0,0)))
    ).length;

    return (
        <>
            <PageHeader title="Hearings"
                subtitle="Your upcoming appearances — completed/past hearings hidden by default."/>
            {hiddenCount > 0 && (
                <div className="mb-3 inline-flex items-center gap-3 text-xs text-muted-foreground border border-border px-3 py-2">
                    <span>{hiddenCount} completed/past hearing{hiddenCount === 1 ? "" : "s"} hidden.</span>
                    <button onClick={() => setShowCompleted(s => !s)} data-testid="portal-hearings-toggle-completed"
                        className="text-foreground underline hover:no-underline">
                        {showCompleted ? "Hide them" : "Show them too"}
                    </button>
                </div>
            )}
            {visible.length === 0 ? <EmptyState title="No upcoming hearings"/> : (
                <div className="border border-border divide-y divide-border">
                    {visible.map(h => (
                        <div key={h.id} className="px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
                            <div>
                                <div className="font-mono text-sm text-primary">{fmtDateTime(h.scheduled_at)}</div>
                                <div className="text-sm mt-1">{h.purpose || "Hearing"}</div>
                                <div className="text-xs text-muted-foreground">{h.court || "—"}</div>
                            </div>
                            <StatusBadge status={h.status}/>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
}

export function PortalInvoices() {
    const [items, setItems] = useState([]);
    const [paying, setPaying] = useState(null);
    const [showPaid, setShowPaid] = useState(false);

    const load = () => api.get("/portal/invoices").then(r => setItems(r.data));
    useEffect(() => { load(); }, []);

    const visible = useMemo(() => {
        const unpaid = items.filter(i => (i.status || "").toLowerCase() !== "paid");
        const base = showPaid ? [...items] : unpaid;
        // A-Z by due date — earliest-due first
        return base.sort((a, b) => {
            const da = a.due_date ? new Date(a.due_date).getTime() : Infinity;
            const dbn = b.due_date ? new Date(b.due_date).getTime() : Infinity;
            return da - dbn;
        });
    }, [items, showPaid]);
    const paidHidden = items.length - items.filter(i => (i.status || "").toLowerCase() !== "paid").length;

    const downloadPdf = async (inv) => {
        try {
            const resp = await api.get(`/portal/invoices/${inv.id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `Invoice-${inv.number}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(apiError(e)); }
    };

    const pay = async (inv) => {
        setPaying(inv.id);
        try {
            const { data: checkout } = await api.post(`/portal/invoices/${inv.id}/checkout`);
            if (checkout.mode === "manual") {
                toast.info("Online payments not available", { description: checkout.message });
                setPaying(null);
                return;
            }
            const ok = await loadRazorpay();
            if (!ok) {
                toast.error("Could not load Razorpay. Please check your network.");
                setPaying(null);
                return;
            }
            const me = await api.get("/portal/me");
            const rzp = new window.Razorpay({
                key: checkout.key_id,
                order_id: checkout.order_id,
                amount: checkout.amount,
                currency: checkout.currency,
                name: me.data.firm?.name || "Law OS",
                description: `Invoice ${inv.number}`,
                prefill: { name: me.data.client?.name, email: me.data.user?.email, contact: me.data.client?.mobile || "" },
                theme: { color: "#D4AF37" },
                handler: async (resp) => {
                    try {
                        await api.post("/portal/payments/verify", {
                            invoice_id: inv.id,
                            razorpay_order_id: resp.razorpay_order_id,
                            razorpay_payment_id: resp.razorpay_payment_id,
                            razorpay_signature: resp.razorpay_signature,
                        });
                        toast.success(`Payment of ₹${(checkout.amount/100).toLocaleString()} received`);
                        load();
                    } catch (e) { toast.error(apiError(e)); }
                    finally { setPaying(null); }
                },
                modal: { ondismiss: () => setPaying(null) },
            });
            rzp.open();
        } catch (e) {
            toast.error(apiError(e));
            setPaying(null);
        }
    };

    return (
        <>
            <PageHeader title="Invoices" subtitle="Open bills first. Download a PDF anytime — payment status is printed on it."/>
            {paidHidden > 0 && (
                <div className="mb-3 inline-flex items-center gap-3 text-xs text-muted-foreground border border-border px-3 py-2">
                    <span>{paidHidden} paid invoice{paidHidden === 1 ? "" : "s"} hidden.</span>
                    <button onClick={() => setShowPaid(s => !s)} data-testid="portal-invoices-toggle-paid"
                        className="text-foreground underline hover:no-underline">
                        {showPaid ? "Hide them" : "Show them too"}
                    </button>
                </div>
            )}
            <PortalInvoicesTable items={visible} paying={paying} pay={pay} downloadPdf={downloadPdf}/>
        </>
    );
}

function PortalInvoicesTable({ items, paying, pay, downloadPdf }) {
    const ctrl = useTableControls(items);
    if (items.length === 0) return <EmptyState title="No invoices"/>;
    return (
        <>
            <FilterChip ctrl={ctrl} className="mb-3"/>
            <div className="border border-border overflow-x-auto">
                <table className="min-w-[720px] w-full text-sm">
                    <thead className="bg-muted border-b border-border">
                        <tr className="text-left">
                            <SortableTh field="number" ctrl={ctrl}>Number</SortableTh>
                            <SortableTh field="amount" ctrl={ctrl}>Amount</SortableTh>
                            <SortableTh field="paid_amount" ctrl={ctrl}>Paid</SortableTh>
                            <SortableTh field="due_date" ctrl={ctrl}>Due</SortableTh>
                            <SortableTh field="status" ctrl={ctrl}>Status</SortableTh>
                            <th className="px-4 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {ctrl.rows.map(i => {
                            const outstanding = i.amount - (i.paid_amount || 0);
                            return (
                                <tr key={i.id} className="border-b border-border" data-testid={`portal-invoice-row-${i.id}`}>
                                    <td className="px-4 py-3 font-mono text-xs text-primary">{i.number}</td>
                                    <td className="px-4 py-3 font-mono">₹{i.amount.toLocaleString()}</td>
                                    <td className="px-4 py-3 font-mono text-muted-foreground">₹{(i.paid_amount || 0).toLocaleString()}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{i.due_date || "—"}</td>
                                    <td className="px-4 py-3"><StatusBadge status={i.status}/></td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="inline-flex items-center gap-2">
                                            <button onClick={() => downloadPdf(i)}
                                                data-testid={`portal-download-btn-${i.id}`}
                                                title="Download invoice PDF (payment status printed)"
                                                className="inline-flex items-center gap-2 border border-border hover:border-primary hover:text-primary px-3 py-1.5 text-xs transition-colors">
                                                <Download className="w-3 h-3" strokeWidth={2}/>PDF
                                            </button>
                                            {i.status !== "paid" && (
                                                <button onClick={() => pay(i)} disabled={paying === i.id} data-testid={`portal-pay-btn-${i.id}`}
                                                    className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary px-4 py-1.5 text-xs font-medium disabled:opacity-50 transition-colors">
                                                    <CreditCard className="w-3 h-3" strokeWidth={2}/>
                                                    {paying === i.id ? "Opening…" : `Pay ₹${outstanding.toLocaleString()}`}
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                                );
                            })}
                    </tbody>
                </table>
            </div>
        </>
    );
}

export function PortalDocuments() {
    const [items, setItems] = useState([]);
    const [cases, setCases] = useState([]);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [form, setForm] = useState({ display_name: "", case_id: "" });
    const fileRef = useRef(null);

    const load = () => api.get("/portal/documents").then(r => setItems(r.data));
    useEffect(() => {
        load();
        api.get("/portal/cases").then(r => setCases(r.data)).catch(() => {});
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        const file = fileRef.current?.files?.[0];
        if (!file) { toast.error("Choose a file"); return; }
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        if (form.display_name) fd.append("display_name", form.display_name);
        if (form.case_id) fd.append("case_id", form.case_id);
        try {
            await api.post("/portal/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Document shared with your firm");
            setUploadOpen(false);
            setForm({ display_name: "", case_id: "" });
            if (fileRef.current) fileRef.current.value = "";
            load();
        } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
    };

    return (
        <>
            <PageHeader
                title="Documents"
                subtitle="Shared by your firm — and anything you'd like to upload to them."
                action={<GoldButton onClick={() => setUploadOpen(true)} data-testid="portal-upload-btn"><Upload className="w-4 h-4 inline mr-2" strokeWidth={2}/>Upload document</GoldButton>}
            />
            {items.length === 0 ? <EmptyState title="No documents yet" subtitle="Anything you upload here goes directly to your advocate." action={<GoldButton onClick={() => setUploadOpen(true)} data-testid="portal-upload-empty-btn">Upload first</GoldButton>}/> : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
                    {items.map(d => (
                        <div key={d.id} className="bg-muted p-5">
                            <div className="flex items-start justify-between mb-3">
                                <div className="font-serif text-lg truncate">{d.display_name || d.original_filename}</div>
                                {d.uploaded_by_client && <span className="overline !text-primary shrink-0 ml-2">YOU</span>}
                            </div>
                            <div className="text-xs text-muted-foreground mb-3 font-mono">{(d.size / 1024).toFixed(1)} KB</div>
                            <div className="text-xs text-muted-foreground mb-4">{fmtDate(d.created_at)}</div>
                            <a href={`${BACKEND_URL}/api/documents/${d.id}/download`} target="_blank" rel="noreferrer"
                                data-testid={`portal-doc-view-${d.id}`}
                                className="block text-center text-xs border border-border hover:border-primary hover:text-primary py-2 transition-colors">
                                View
                            </a>
                        </div>
                    ))}
                </div>
            )}

            <Modal open={uploadOpen} onClose={() => setUploadOpen(false)} title="Upload to your firm" testId="portal-upload-modal">
                <form onSubmit={submit} className="space-y-4">
                    <Field label="Display name" hint="What your advocate will see. Defaults to the filename.">
                        <Input value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })} placeholder="e.g. Aadhaar copy" data-testid="portal-doc-name-input"/>
                    </Field>
                    <Field label="Attach to case (optional)">
                        <Select value={form.case_id} onChange={e => setForm({ ...form, case_id: e.target.value })} data-testid="portal-doc-case-select"
                            options={[{ value: "", label: "— none —" }, ...cases.map(c => ({ value: c.id, label: `${c.internal_number} — ${c.title}` }))]}/>
                    </Field>
                    <Field label="File">
                        <input ref={fileRef} type="file" required data-testid="portal-doc-file-input"
                            className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm text-muted-foreground file:bg-muted file:border-0 file:px-3 file:py-1.5 file:text-foreground file:text-xs file:mr-3 file:cursor-pointer"/>
                    </Field>
                    <div className="flex justify-end gap-2 pt-2">
                        <GhostButton type="button" onClick={() => setUploadOpen(false)}>Cancel</GhostButton>
                        <GoldButton type="submit" disabled={uploading} data-testid="portal-doc-upload-btn">{uploading ? "Uploading…" : "Upload"}</GoldButton>
                    </div>
                </form>
            </Modal>
        </>
    );
}
